import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  name: "",
  type: "ladies_suit_outlet",
  address: "",
  phone: "",
  business_category: "",
  // Linking choice:
  //   "default"  → joins org's default group (back-compat)
  //   "linked"   → joins an existing group (linked_group_id required)
  //   "independent" → fresh group of one (full isolation)
  linking_mode: "default",
  linked_group_id: "",
};

export default function Branches() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });

  const load = async () => {
    const [b, g] = await Promise.all([api.get("/branches"), api.get("/linked-groups").catch(() => ({ data: [] }))]);
    setItems(b.data);
    setGroups(g.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      name: form.name,
      type: form.type,
      address: form.address,
      phone: form.phone,
      business_category: form.business_category,
    };
    if (form.linking_mode === "linked") {
      if (!form.linked_group_id) {
        toast.error("Pick a group to link to");
        return;
      }
      body.linked_group_id = form.linked_group_id;
    } else if (form.linking_mode === "independent") {
      body.independent = true;
    }
    try {
      await api.post("/branches", body);
      toast.success("Branch created");
      setOpen(false);
      setForm({ ...BLANK });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this branch? Existing transactions remain in the database but the branch is marked deleted.")) return;
    try { await api.delete(`/branches/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Group branches by linked_group_id for display
  const grouped = {};
  for (const b of items) {
    const gid = b.linked_group_id || "ungrouped";
    grouped[gid] = grouped[gid] || [];
    grouped[gid].push(b);
  }

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3">
        <div className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-3xl">
          <b className="text-slate-900">Linked branches</b> share parties, products and purchase bills across the group (great when multiple outlets share suppliers and inventory).
          <b className="text-slate-900 ml-2">Independent branches</b> are fully isolated — separate parties, products, purchases. Sales remain branch-specific in both modes.
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-branch-btn">
              <Plus className="w-4 h-4 mr-2" />Add Branch
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Branch</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="branch-name" /></div>
              <div>
                <Label>Business Category</Label>
                <Input placeholder="e.g. Clothing, Shoes, Accessories" value={form.business_category}
                  onChange={(e) => setForm({ ...form, business_category: e.target.value })}
                  className="rounded-none mt-1" data-testid="branch-category" />
                <div className="text-[11px] text-slate-500 mt-1">Optional tag, useful for grouping in BI dashboards.</div>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" /></div>

              <div className="border-t border-slate-200 pt-3">
                <Label>Data linking</Label>
                <div className="text-[11px] text-slate-500 mb-2">Choose whether this branch shares parties/products/purchase data with existing branches, or operates independently.</div>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 p-2 border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="link-mode-default">
                    <input type="radio" name="link" checked={form.linking_mode === "default"} onChange={() => setForm({ ...form, linking_mode: "default" })} className="mt-0.5" />
                    <div>
                      <div className="text-sm font-medium">Default — join existing group</div>
                      <div className="text-[11px] text-slate-500">Shares data with all other default branches in this org.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="link-mode-linked">
                    <input type="radio" name="link" checked={form.linking_mode === "linked"} onChange={() => setForm({ ...form, linking_mode: "linked" })} className="mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium flex items-center gap-1"><Link2 size={12} /> Link to a specific group</div>
                      <div className="text-[11px] text-slate-500">Shares parties / products / purchase data with selected sibling branches.</div>
                      {form.linking_mode === "linked" && (
                        <select
                          value={form.linked_group_id}
                          onChange={(e) => setForm({ ...form, linked_group_id: e.target.value })}
                          className="mt-2 h-8 px-2 border border-slate-300 text-xs w-full"
                          data-testid="link-group-select"
                        >
                          <option value="">— pick a group —</option>
                          {groups.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.branches.map((b) => b.name).join(", ") || "(empty)"}
                              {g.categories?.length ? ` · ${g.categories.join(", ")}` : ""}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>
                  <label className="flex items-start gap-2 p-2 border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="link-mode-independent">
                    <input type="radio" name="link" checked={form.linking_mode === "independent"} onChange={() => setForm({ ...form, linking_mode: "independent" })} className="mt-0.5" />
                    <div>
                      <div className="text-sm font-medium flex items-center gap-1"><Unlink size={12} /> Independent</div>
                      <div className="text-[11px] text-slate-500">Fresh, isolated group — no shared data with any existing branch. Good for a different business vertical.</div>
                    </div>
                  </label>
                </div>
              </div>

              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="branch-save">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Branch list, grouped by linked_group_id */}
      <div className="space-y-6">
        {items.length === 0 && <div className="text-slate-500">No branches yet.</div>}
        {Object.entries(grouped).map(([gid, list]) => (
          <div key={gid} data-testid={`branch-group-${gid}`}>
            <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-[0.2em] text-slate-500">
              {list.length > 1 ? <Link2 size={12} /> : <Unlink size={12} />}
              {list.length > 1 ? `Linked group · ${list.length} branches share data` : `Independent · ${list[0].name}`}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {list.map((b) => (
                <div key={b.id} className="bg-white border border-slate-200 rounded-sm p-4 sm:p-5" data-testid={`branch-card-${b.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{b.name}</div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge className="rounded-none bg-slate-100 text-slate-700">{b.type.replace(/_/g, " ")}</Badge>
                        {b.business_category && <Badge className="rounded-none bg-blue-100 text-blue-700">{b.business_category}</Badge>}
                      </div>
                      {b.address && <div className="text-xs text-slate-500 mt-2 break-words">{b.address}</div>}
                      {b.phone && <div className="text-xs text-slate-500">{b.phone}</div>}
                    </div>
                    <Button size="sm" variant="ghost" className="rounded-none text-red-600 flex-shrink-0" onClick={() => remove(b.id)} data-testid={`delete-branch-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
